import React, { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Grid, Typography } from '@material-ui/core';
import { CustomTheme } from 'common/ui/interfaces';
import { BookingCalendar, EventDetailsModal } from '../../components';
import { useCalendarSharingParams } from './useCalendarSharingParams';
import { useEventListQuery } from './useEventListQuery';
import { useEventDetailsQuery } from './useEventDetailsQuery';
import { PeakModules } from 'common/constants/peakModules';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';
import {
  BookingNotificationsEvents,
  EShortNotificationType,
} from 'modules/booking/constants/notificationType';
import { fetchSenderAvailabilityThunk } from 'modules/booking/state/senderAvailability/actions';
import { DEFAULT_DATE_FORMAT } from 'common/constants/dateFormats';

const useStyles = makeStyles((theme: CustomTheme) => ({
  root: {
    height: '100vh',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    padding: theme.spacing(2, 0),
  },
  calendarContainer: {
    flex: 1,
  },
  calendar: {
    height: 'auto',
    maxHeight: 'calc(100vh - 93px)',
    flex: 1,
  },
}));

const BookingShared: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedEvent, setSelectedEvent] = useState<{ id: string; time: string } | null>(null);
  const params = useCalendarSharingParams();
  const { data, isLoading } = useEventListQuery(params);

  const { eventDetails, isLoading: isDetailsLoading } = useEventDetailsQuery(
    params.corporation,
    selectedEvent,
  );

  const onLoadSenderAvailability = useCallback(
    (type: SenderAvailabilityTypeList, shortTypeEvent: EShortNotificationType) => {
      dispatch(
        fetchSenderAvailabilityThunk([type], {
          module: PeakModules.Booking,
          events: [BookingNotificationsEvents[shortTypeEvent]],
        }),
      );
    },
    [dispatch],
  );

  const handleEventClickEvent = useCallback(({ eventId, startDate }) => {
    setSelectedEvent({
      id: eventId,
      time: startDate,
    });
  }, []);

  return (
    <Grid className={classes.root} item xs={12}>
      <Box className={classes.calendarContainer} display="flex" flexDirection="column" flex="1">
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography className={classes.title} component="h1" variant="h2">
              {`${moment(params.rangeStartDate).format(DEFAULT_DATE_FORMAT)} - ${moment(
                params.rangeEndDate,
              ).format(DEFAULT_DATE_FORMAT)}`}
            </Typography>
          </Grid>
        </Grid>

        <BookingCalendar
          bookingEvents={data.toJS()}
          initialView={params.calendarView}
          isLoading={isLoading}
          onEventClick={handleEventClickEvent}
          className={classes.calendar}
        />

        <EventDetailsModal
          anchorEl={selectedEvent ? document.body : undefined}
          readOnly
          module={PeakModules.Booking}
          eventId={selectedEvent?.id}
          eventDetails={eventDetails?.toJS() || undefined}
          isEventDetailsLoading={isDetailsLoading}
          onLoadEvent={() => null}
          onClose={() => setSelectedEvent(null)}
          type={SenderAvailabilityTypeList.COMMON}
          onLoadSenderAvailability={onLoadSenderAvailability}
        />
      </Box>
    </Grid>
  );
};

export default BookingShared;
